<template>
  <v-card
    flat
    tile
  >
    <v-btn
      x-small
      absolute
      depressed
      fab
      style="right:-24px;top:-24px"
      @click.native="$emit('close')"
    >
      <v-icon>
        mdi-close
      </v-icon>
    </v-btn>
    <v-window v-model="currentFeature">
      <v-window-item
        v-for="(feature, i) in features"
        :key="`card-${i}`"
      >
        <div :style="`max-height:${windowHeight/2.5}px;overflow-y:auto`">
          <div
            v-if="config.noTableInTooltip"
            :style="`max-width:${Math.max(windowWidth / 3, 300)}px;`"
          >
            <div
              v-for="field in config.tooltip"
              :key="field"
              style="white-space: pre;"
            >
              <span class="font-weight-bold">
                {{ fields[field].title || fields[field]['x-originalName'] || fields[field].key }} :
              </span>
              <a
                v-if="linkField && linkField.key === field && feature[field]"
                :href="(!feature[field].includes('http') ? 'https://' : '') + feature[field].replace('http:', 'https:')"
                target="_blank"
              >{{ feature[field].replace('http:', 'https:') }}</a>
              <a
                v-else-if="feature[field] && fields[field]['x-refersTo'] === 'https://www.w3.org/2006/vcard/ns#tel'"
                :href="'tel:' + feature[field]"
                target="_blank"
              >
                {{ feature[field] }}
              </a>
              <a
                v-else-if="feature[field] && fields[field]['x-refersTo'] === 'https://www.w3.org/2006/vcard/ns#email'"
                :href="'mailto:' + feature[field]"
                target="_blank"
              >
                {{ feature[field] }}
              </a>
              <template v-else>
                {{ formatField(feature, fields[field]) }}
              </template>
            </div>
          </div>
          <table
            v-else
            :style="`max-width:${Math.max(windowWidth/3, 300)}px;`"
          >
            <tbody>
              <tr
                v-for="field in config.tooltip"
                :key="field"
                :style="`padding:0;max-width:${Math.max(windowWidth/3, 300)}px`"
              >
                <td
                  :style="`text-align:right;width:${100*maxFieldTitleLength/(maxFieldTitleLength + maxFieldValueLength)}%`"
                  class="font-weight-bold"
                >
                  {{ fields[field].title || fields[field]['x-originalName'] || fields[field].key }}
                </td>
                <td>&nbsp;:&nbsp;</td>
                <td :style="`width:${100*maxFieldValueLength/(maxFieldTitleLength + maxFieldValueLength)}%;white-space: pre;`">
                  <a
                    v-if="linkField && linkField.key === field && feature[field]"
                    :href="(!feature[field].includes('http') ? 'https://' : '') + feature[field].replace('http:','https:')"
                    target="_blank"
                  >{{ feature[field].replace('http:','https:') }}</a>
                  <a
                    v-else-if="feature[field] && fields[field]['x-refersTo'] === 'https://www.w3.org/2006/vcard/ns#tel'"
                    :href="'tel:' + feature[field]"
                    target="_blank"
                  >
                    {{ feature[field] }}
                  </a>
                  <a
                    v-else-if="feature[field] && fields[field]['x-refersTo'] === 'https://www.w3.org/2006/vcard/ns#email'"
                    :href="'mailto:' + feature[field]"
                    target="_blank"
                  >
                    {{ feature[field] }}
                  </a>
                  <template v-else>
                    {{ formatField(feature, fields[field]) }}
                  </template>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </v-window-item>
    </v-window>

    <v-card-actions
      v-if="features.length > 1"
      class="justify-space-between"
    >
      <v-btn
        icon
        @click="prev"
      >
        <v-icon>mdi-chevron-left</v-icon>
      </v-btn>
      <v-item-group
        v-model="currentFeature"
        :style="`max-width:${Math.max(windowWidth/3, 300)}px`"
        class="text-center"
        mandatory
      >
        <v-item
          v-for="(feature, n) in features"
          :key="`btn-${n}`"
        >
          <v-btn
            slot-scope="{ active, toggle }"
            :input-value="active"
            :style="`width:${radius(feature, active)}px;height:${radius(feature, active)}px`"
            icon
            @click="toggle"
          >
            <v-icon
              :size="radius(feature, active)"
              :style="`color:${color(feature)}`"
            >
              mdi-circle
            </v-icon>
          </v-btn>
        </v-item>
      </v-item-group>
      <v-btn
        icon
        @click="next"
      >
        <v-icon>mdi-chevron-right</v-icon>
      </v-btn>
    </v-card-actions>
  </v-card>
</template>

<script>
import { mapState, mapGetters } from 'vuex'
import colors from '../assets/colors'
import { formatField } from '@data-fair/components-app/format/fields'

export default {
  props: {
    features: { type: Array, required: true }
  },
  data: () => ({
    currentFeature: 0
  }),
  computed: {
    ...mapState(['dataset', 'quantiles', 'colorMetrics']),
    ...mapGetters(['config', 'fields', 'radiusMetrics', 'linkField']),
    colorField () {
      if (this.config.colors.type === 'enum' && this.config.colors.enumOptions.field) return this.config.colors.enumOptions.field
      else if (this.config.colors.type === 'range' && this.config.colors.rangeOptions.field) return this.config.colors.rangeOptions.field
      return null
    },
    maxFieldTitleLength () {
      return Math.max(...this.config.tooltip.map(field => (this.fields[field].title || this.fields[field]['x-originalName'] || this.fields[field].key).length))
    },
    maxFieldValueLength () {
      return Math.max(...[].concat(...this.features.map(feature => this.config.tooltip.map(field => this.formatField(feature, this.fields[field]).length))))
    }
  },
  async mounted () {
    await this.$nextTick()
    document.activeElement.blur()
  },
  methods: {
    next () {
      this.currentFeature = Math.min(this.currentFeature + 1, this.features.length - 1)
    },
    prev () {
      this.currentFeature = Math.max(this.currentFeature - 1, 0)
    },
    color (feature) {
      if (!this.colorField) {
        return this.config.colors.value
      } else if (this.config.colors.type === 'enum') {
        const value = feature[this.colorField]
        if (value !== undefined) return colors(this.config.colors).find(c => c.value === value + '').color
        return this.config.colors.default
      } else if (this.config.colors.type === 'range') {
        const value = feature[this.colorField]
        if (value !== undefined) return colors(this.config.colors, this.colorMetrics?.min, this.colorMetrics?.max, this.quantiles).find(c => c.min <= value && c.max >= value).color
        return this.config.colors.default
      }
    },
    radius (feature, active) {
      if (this.config.radius.type === 'variable') {
        const alpha = ((feature[this.config.radius.field] !== undefined ? feature[this.config.radius.field] : this.radiusMetrics.avg) - this.radiusMetrics.min) / (this.radiusMetrics.max - this.radiusMetrics.min)
        return active ? (11 * (1 - alpha) + alpha * 28) : (6 * (1 - alpha) + alpha * 16)
      } else return active ? 14 : 8
    },
    formatField
  }
}
</script>
